import React, { useState, useEffect } from "react";

import CloseSVG from "../../../../images/icons/svg/close.svg";
import paigeReviewService from "../../../../services/PaigeReview";
import FindingCard from "./FindingCard";
import PageInsightsSection from "./PageInsightsSection";

import styles from "./PaigeReview.module.scss";

var PaigeReview = function (props) {
  var docId = props.docId;
  var closePanel = props.closePanel;
  var onFindingClick = props.onFindingClick;
  var onAnalysisUpdate = props.onAnalysisUpdate;

  // State
  var _analysis = useState(null);
  var analysis = _analysis[0];
  var setAnalysis = _analysis[1];

  var _analysisType = useState(null); // "paab", "fda", "illumina", or "menopur"
  var analysisType = _analysisType[0];
  var setAnalysisType = _analysisType[1];

  var _isLoading = useState(true);
  var isLoading = _isLoading[0];
  var setIsLoading = _isLoading[1];

  // Load analysis on mount
  useEffect(
    function () {
      loadAnalysis();
    },
    [docId]
  );

  // Notify parent when analysis changes
  useEffect(
    function () {
      if (analysis && onAnalysisUpdate) {
        onAnalysisUpdate(analysis);
      }
    },
    [analysis]
  );

  // Poll for status when analysis is pending or processing
  useEffect(
    function () {
      if (!analysis || !analysisType) return;
      if (analysis.status !== "pending" && analysis.status !== "processing") {
        return;
      }

      var pollInterval = setInterval(function () {
        var getAnalysisFn = getAnalysisFnForType(analysisType);

        getAnalysisFn(analysis.id)
          .then(function (response) {
            setAnalysis(response.data);
          })
          .catch(function (error) {
            console.error("Error polling analysis status:", error);
          });
      }, 3000);

      return function () {
        clearInterval(pollInterval);
      };
    },
    [analysis, analysisType]
  );

  /**
   * Returns the correct getAnalysis service function for the given type
   * @param {string} type - "paab", "fda", "illumina", or "menopur"
   * @returns {Function}
   */
  var getAnalysisFnForType = function (type) {
    if (type === "fda") {
      return paigeReviewService.getFdaAnalysis;
    }
    if (type === "illumina") {
      return paigeReviewService.getIlluminaAnalysis;
    }
    if (type === "menopur") {
      return paigeReviewService.getMenopurAnalysis;
    }
    return paigeReviewService.getPaabAnalysis;
  };

  var loadAnalysis = function () {
    setIsLoading(true);

    // Fetch PAAB, FDA, Illumina, and Menopur analyses in parallel
    Promise.all([
      paigeReviewService
        .getPaabAnalysesForDocument(docId)
        .catch(function (err) {
          console.log("PAAB analyses fetch failed:", err);
          return { data: [] };
        }),
      paigeReviewService.getFdaAnalysesForDocument(docId).catch(function (err) {
        console.log("FDA analyses fetch failed:", err);
        return { data: [] };
      }),
      paigeReviewService
        .getIlluminaAnalysesForDocument(docId)
        .catch(function (err) {
          console.log("Illumina analyses fetch failed:", err);
          return { data: [] };
        }),
      paigeReviewService
        .getMenopurAnalysesForDocument(docId)
        .catch(function (err) {
          console.log("Menopur analyses fetch failed:", err);
          return { data: [] };
        }),
    ])
      .then(function (results) {
        var paabAnalyses = results[0].data || [];
        var fdaAnalyses = results[1].data || [];
        var illuminaAnalyses = results[2].data || [];
        var menopurAnalyses = results[3].data || [];

        // Determine which analysis to use (most recent from any type)
        var candidates = [];
        if (paabAnalyses.length > 0) {
          candidates.push({ analysis: paabAnalyses[0], type: "paab" });
        }
        if (fdaAnalyses.length > 0) {
          candidates.push({ analysis: fdaAnalyses[0], type: "fda" });
        }
        if (illuminaAnalyses.length > 0) {
          candidates.push({ analysis: illuminaAnalyses[0], type: "illumina" });
        }
        if (menopurAnalyses.length > 0) {
          candidates.push({ analysis: menopurAnalyses[0], type: "menopur" });
        }

        if (candidates.length === 0) {
          setIsLoading(false);
          return;
        }

        // Pick the most recently created analysis
        var selected = candidates.reduce(function (best, current) {
          var bestDate = new Date(best.analysis.created_at);
          var currentDate = new Date(current.analysis.created_at);
          return currentDate > bestDate ? current : best;
        });

        var selectedType = selected.type;
        var selectedAnalysis = selected.analysis;

        setAnalysisType(selectedType);

        // Fetch full analysis with findings
        var getAnalysisFn = getAnalysisFnForType(selectedType);

        getAnalysisFn(selectedAnalysis.id)
          .then(function (response) {
            if (response && response.data) {
              setAnalysis(response.data);
            }
          })
          .catch(function (error) {
            console.error("Error fetching full analysis:", error);
          })
          .finally(function () {
            setIsLoading(false);
          });
      })
      .catch(function (error) {
        console.error("Error loading analyses list:", error);
        setIsLoading(false);
      });
  };

  var renderSummary = function () {
    if (!analysis) return null;

    return (
      <div className={styles.summary}>
        <div className={styles.summaryItem + " " + styles.critical}>
          <span className={styles.summaryCount}>{analysis.critical_count}</span>
          <span className={styles.summaryLabel}>Critical</span>
        </div>
        <div className={styles.summaryItem + " " + styles.warning}>
          <span className={styles.summaryCount}>{analysis.warning_count}</span>
          <span className={styles.summaryLabel}>Warnings</span>
        </div>
        <div className={styles.summaryItem + " " + styles.info}>
          <span className={styles.summaryCount}>{analysis.info_count}</span>
          <span className={styles.summaryLabel}>Info</span>
        </div>
      </div>
    );
  };

  var renderFindings = function () {
    if (!analysis || !analysis.findings) return null;

    var findings = analysis.findings;

    if (findings.length === 0) {
      return (
        <div className={styles.noFindings}>
          No issues found in this analysis.
        </div>
      );
    }

    // Sort by page number ascending
    var sortByPage = function (a, b) {
      var pageA = a.page_number != null ? a.page_number : Infinity;
      var pageB = b.page_number != null ? b.page_number : Infinity;
      return pageA - pageB;
    };

    // Group findings by severity and sort each group by page number
    var criticalFindings = findings
      .filter(function (f) {
        return f.severity === "critical";
      })
      .sort(sortByPage);
    var warningFindings = findings
      .filter(function (f) {
        return f.severity === "warning";
      })
      .sort(sortByPage);
    var infoFindings = findings
      .filter(function (f) {
        return f.severity === "info";
      })
      .sort(sortByPage);

    return (
      <div className={styles.findingsList}>
        {criticalFindings.length > 0 && (
          <div className={styles.findingsGroup}>
            <div className={styles.findingsGroupHeader}>Critical Issues</div>
            {criticalFindings.map(function (finding) {
              return (
                <FindingCard
                  key={finding.id}
                  finding={finding}
                  analysisType={analysisType}
                  onFindingClick={onFindingClick}
                />
              );
            })}
          </div>
        )}
        {warningFindings.length > 0 && (
          <div className={styles.findingsGroup}>
            <div className={styles.findingsGroupHeader}>Warnings</div>
            {warningFindings.map(function (finding) {
              return (
                <FindingCard
                  key={finding.id}
                  finding={finding}
                  analysisType={analysisType}
                  onFindingClick={onFindingClick}
                />
              );
            })}
          </div>
        )}
        {infoFindings.length > 0 && (
          <div className={styles.findingsGroup}>
            <div className={styles.findingsGroupHeader}>Info</div>
            {infoFindings.map(function (finding) {
              return (
                <FindingCard
                  key={finding.id}
                  finding={finding}
                  analysisType={analysisType}
                  onFindingClick={onFindingClick}
                />
              );
            })}
          </div>
        )}
      </div>
    );
  };

  var renderContent = function () {
    if (isLoading) {
      return (
        <div className={styles.loadingState}>
          <div className={styles.spinner}></div>
          <div>Loading analysis...</div>
        </div>
      );
    }

    if (!analysis) {
      return (
        <div className={styles.emptyState}>
          <div className={styles.emptyStateTitle}>No analysis available</div>
          <div className={styles.emptyStateText}>
            This document has not been analyzed yet.
          </div>
        </div>
      );
    }

    if (analysis.status === "pending" || analysis.status === "processing") {
      return (
        <div className={styles.processingState}>
          <div className={styles.spinner}></div>
          <div className={styles.processingTitle}>Analysis in progress</div>
          <div className={styles.processingText}>
            This may take a few minutes. The results will appear automatically
            when complete.
          </div>
        </div>
      );
    }

    if (analysis.status === "failed") {
      return (
        <div className={styles.errorState}>
          <div className={styles.errorTitle}>Analysis failed</div>
          <div className={styles.errorText}>
            {analysis.error_message || "An error occurred during analysis."}
          </div>
        </div>
      );
    }

    // Both Illumina and PAAB analyses return page_insights describing the
    // products detected on each page. PAAB detection is text-only, so its
    // insights are a simpler subset of the Illumina shape.
    var supportsPageInsights =
      analysisType === "illumina" || analysisType === "paab";
    var hasPageInsights =
      supportsPageInsights &&
      analysis.page_insights &&
      analysis.page_insights.length > 0;

    return (
      <>
        {renderSummary()}
        {hasPageInsights && (
          <PageInsightsSection pageInsights={analysis.page_insights} />
        )}
        {renderFindings()}
      </>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.title}>Paige Review</div>
        </div>
        <div className={styles.headerRight}>
          <div className={styles.closeButton} onClick={closePanel}>
            <img src={CloseSVG} alt="Close" />
          </div>
        </div>
      </div>

      <div className={styles.body}>{renderContent()}</div>
    </div>
  );
};

export default PaigeReview;
